// src/pages/VotePage.js
// eslint-disable-next-line no-unused-vars
import React, {useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import {useAuth} from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {getUserVotes, postVotePoints} from '../api/axios';
import { Navigate} from 'react-router-dom';


const VotePage = () => {
  const { t } = useTranslation();
  const { user, refreshUserData } = useAuth();
  const [notification, setNotification] = useState('');
  const [userVotes, setUserVotes] = useState({});
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    getUserVotes()
      .then(response => {
        const data = response.data;
        const mapping = {};
        if (data && Array.isArray(data.votes)) {
          data.votes.forEach(v => {
            const lowerSite = v.vote_site.toLowerCase();
            mapping[lowerSite] = v.next_vote_time;
          });
        }
        setUserVotes(mapping);

        if (data.vote_time) {
          const lastVoteTime = new Date(data.vote_time).getTime();
          if (Date.now() - lastVoteTime < 2 * 60 * 1000) {
            setNotification(t('votePage.thankYouNotification'));
          } else {
            setNotification('');
          }
        }
      })
      .catch(error => {
        // Fehlerbehandlung, falls erforderlich
      });
    refreshUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Konvertiere Datum im Format "YYYY-MM-DD HH:MM:SS" in ein Date-Objekt (UTC)
  const parseDateToUTC = dateStr => {
    if (!dateStr) return new Date(0);
    const trimmed = dateStr.trim();
    const parts = trimmed.split(' ');
    if (parts.length < 2) return new Date(0);
    return new Date(`${parts[0]}T${parts[1]}.000Z`);
  };

  const getVoteMessage = futureTimeStr => {
    if (!futureTimeStr) return t('votePage.canVoteNow');
    const futureTime = parseDateToUTC(futureTimeStr).getTime() - 3600000;
    if (Date.now() >= futureTime) return t('votePage.canVoteNow');
    return t('votePage.nextVote', { time: new Date(futureTime).toLocaleString() });
  };

  const voteSites = [
    {
      id: 1,
      name: 'mmtop200',
      url: 'https://mmtop200.com/vote/4664/' + (user ? user.username : ''),
      icon: '/assets/vote/mmtop200.jpg',
      description: t('votePage.voteSites.mmtop200Description'),
      cycle: 12, // hours
      points: 3,
    },
    {
      id: 2,
      name: 'gtop100',
      url: 'https://gtop100.com/Rappelz/Arcane-Rappelz-104532?vote&pingUsername=' + (user ? user.username : ''),
      icon: '/assets/vote/gtop100.jpg',
      description: t('votePage.voteSites.gtop100Description'),
      cycle: 24, // hours
      points: 6,
    },
    {
      id: 3,
      name: 'xtremetop100',
      url: 'https://www.xtremetop100.com/in.php?site=1132376985',
      icon: '/assets/vote/xtremetop100.jpg',
      description: t('votePage.voteSites.xtremetop100Description'),
      cycle: 12, // hours
      points: 3,
    },
  ];

  const handleVote = async site => {
    if (!user) return;
    try {
      const response = await postVotePoints(site.name, user.username);
      if (response.data && response.data.success) {
        setNotification(t('votePage.thankYouNotification'));
        window.open(site.url, '_blank');

        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
        }

        let count = 0;
        refreshTimerRef.current = setInterval(() => {
          refreshUserData();
          count++;
          if (count >= 6) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
          }
        }, 5000);
      } else {
        setNotification(response.data.error || t('votePage.voteError'));
      }
    } catch (error) {
      // Fehlerbehandlung, falls erforderlich
    }
  };


    if (!user) {
      return <Navigate to="/" replace />;
    }

  return (
    <PageContentComponent>
      <PageContainerTitleComponent title={t('votePage.pageTitle')} />
      <ContentWrapper>
        {notification && <Notification>{notification}</Notification>}
        <Intro>
          <h2>{t('votePage.introTitle')}</h2>
          <p>{t('votePage.introDescription')}</p>
        </Intro>
        <VoteList>
          {voteSites.map(site => (
            <VoteCard key={site.id}>
              <VoteIcon src={site.icon} alt={site.name} />
              <VoteInfo>
                <VoteName>{site.name}</VoteName>
                <VoteDescription>{site.description}</VoteDescription>
                <VoteButton onClick={() => handleVote(site)}>
                  {t('votePage.voteNow')}
                </VoteButton>
                <ExtraInfo>
                  <InfoText>
                    {t('votePage.extraInfo', { cycle: site.cycle, points: site.points })}
                  </InfoText>
                  <Countdown>{getVoteMessage(userVotes[site.name.toLowerCase()])}</Countdown>
                </ExtraInfo>
              </VoteInfo>
            </VoteCard>
          ))}
        </VoteList>
      </ContentWrapper>
    </PageContentComponent>
  );
};

export default VotePage;

const ContentWrapper = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Intro = styled.div`
  text-align: center;
  margin-bottom: 30px;
  h2 {
    font-size: 2rem;
    margin-bottom: 10px;
  }
  p {
    font-size: 1.2rem;
  }
`;

const VoteList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
`;

const VoteCard = styled.div`
  background-image: url(/assets/ui/container-big-bg.png);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  border-radius: 8px;
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const VoteIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 15px;
`;

const VoteInfo = styled.div`
  text-align: center;
  display: contents;
`;

const VoteName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const VoteDescription = styled.p`
  font-size: 1rem;
  margin-bottom: 15px;
  height: 72px;
`;

const VoteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10pt;
  padding: 0 20px;
  border: none;
  border-radius: 0;
  background-image: url('/assets/ui/btn.png');
  background-repeat: no-repeat;
  background-position: center;
  background-color: transparent;
  background-size: 100% 100%;
  opacity: 0.8;
  font-weight: bold;
  text-transform: uppercase;
  height: 66px;
  width: 150px;
  cursor: pointer;
`;

const ExtraInfo = styled.div`
  margin-top: 10px;
  font-size: 0.9rem;
`;

const InfoText = styled.div`
  margin-bottom: 5px;
`;

const Countdown = styled.div`
  font-weight: bold;
  color: #007bff;
`;

const Notification = styled.div`
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
`;
